// ─── « Tutoriel & Mise en Pratique » (Core) — porté de MoPCal ─────
// Vue d'ensemble : progression (tutoriels vus / mises en pratique faites) +
// liste des tutoriels (issus du registre d'aide) et des exercices (registre de
// pratiques). Rendue comme SECTION (injectable dans Profil) : les tutoriels
// s'ouvrent via la feuille d'aide de la fonction ; « Faire » lance le runner
// (BarrePratique, montée au niveau app) et marque la progression au Terminer.
import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, Platform } from 'react-native';
import { useUI } from '../theme/PreferencesUI';
import { useT } from '../i18n/LangueContext';
import { useAide } from '../aide/AideContext';
import Feuille from '../ui/Feuille';
import { usePratique } from './PratiqueContext';

// Anneau de progression SIMPLE (sans dépendance SVG) : pourcentage au centre
// d'un anneau teinté + barre de détail dessous.
function Progres({ pct, label, sousLabel, couleur, UI, s }) {
  return (
    <View style={s.progBloc}>
      <View style={[s.anneau, { borderColor: couleur + '33' }]}>
        <View style={[s.anneauCoiffe, { borderColor: couleur, opacity: Math.max(0.15, pct / 100) }]} />
        <Text style={[s.anneauPct, { color: couleur }]}>{pct}%</Text>
      </View>
      <Text style={s.progLbl}>{label}</Text>
      <Text style={s.progSous}>{sousLabel}</Text>
      <View style={s.barre}><View style={[s.barreFill, { width: `${pct}%`, backgroundColor: couleur }]} /></View>
    </View>
  );
}

export default function SectionApprentissage() {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const { contenus } = useAide();
  const p = usePratique();
  const [tutoOuvert, setTutoOuvert] = useState(null);   // { cle, aide, tuto }

  // Tutoriels = tutos déclarés dans le registre d'aide (clé = fonction.tutoIdx).
  const tutos = useMemo(() => Object.entries(contenus || {})
    .flatMap(([f, aide]) => (aide.tutos || []).map((tuto, i) => ({
      fonction: f, aide, tuto, cle: `${f}.t${i}`, emoji: aide.emoji || '🎓',
    }))), [contenus]);

  const vusTut = tutos.filter(x => p.apprentissage.tutos[x.cle]).length;
  const pctTut = tutos.length ? Math.round((vusTut / tutos.length) * 100) : 0;
  const faitesMep = p.liste.filter(m => p.apprentissage.mep[m.cle]).length;
  const pctMep = p.liste.length ? Math.round((faitesMep / p.liste.length) * 100) : 0;

  const tr = (v) => (typeof v === 'string' ? t(v) : v);

  const feuilleTuto = tutoOuvert && (
    <Feuille visible titre={tr(tutoOuvert.tuto.titre)} emoji={tutoOuvert.emoji} hauteur={0.7}
      onFermer={() => setTutoOuvert(null)}
      action={{ icone: '✓', label: t('tuto.terminer'), onPress: () => { p.marquerTuto(tutoOuvert.cle); setTutoOuvert(null); } }}>
      {(tutoOuvert.tuto.etapes || []).map((et, k) => (
        <View key={k} style={s.etape}>
          <Text style={s.etapeNum}>{k + 1}</Text>
          <Text style={s.etapeTxt}>{tr(et)}</Text>
        </View>
      ))}
    </Feuille>
  );

  return (
    <View>
      <Text style={s.intro}>{t('apprentissage.intro')}</Text>

      {/* Progression (2 anneaux). */}
      <View style={s.dash}>
        <Progres pct={pctTut} label={t('apprentissage.tutoriels')}
          sousLabel={`${vusTut} ${t('apprentissage.vus').toLowerCase()} · ${tutos.length - vusTut} ${t('apprentissage.aVoir').toLowerCase()}`}
          couleur={UI.accent} UI={UI} s={s} />
        <Progres pct={pctMep} label={t('apprentissage.misesEnPratique')}
          sousLabel={`${faitesMep} ${t('apprentissage.faites').toLowerCase()} · ${p.liste.length - faitesMep} ${t('apprentissage.aFaire').toLowerCase()}`}
          couleur="#3FB27F" UI={UI} s={s} />
      </View>

      {/* Tutoriels (lecture guidée). */}
      <Text style={s.groupe}>🎓 {t('apprentissage.tutoriels')}</Text>
      {tutos.map(x => {
        const vu = !!p.apprentissage.tutos[x.cle];
        return (
          <View key={x.cle} style={s.ligne}>
            <Text style={s.ligneEmoji}>{x.emoji}</Text>
            <Text style={[s.ligneTitre, vu && s.ligneFaite]} numberOfLines={1}>{tr(x.tuto.titre)}</Text>
            {vu && <Text style={s.coche}>✓</Text>}
            <TouchableOpacity style={s.btn} onPress={() => setTutoOuvert(x)}>
              <Text style={s.btnTxt}>{vu ? t('apprentissage.revoir') : t('apprentissage.voir')}</Text>
            </TouchableOpacity>
          </View>
        );
      })}

      {/* Mises en pratique (exercices guidés). */}
      <Text style={s.groupe}>🏋️ {t('apprentissage.misesEnPratique')}</Text>
      {p.liste.length === 0 && <Text style={s.vide}>{t('apprentissage.mepAVenir')}</Text>}
      {p.liste.map(m => {
        const faite = !!p.apprentissage.mep[m.cle];
        return (
          <View key={m.cle} style={s.ligne}>
            <Text style={s.ligneEmoji}>{m.emoji || '🏋️'}</Text>
            <View style={{ flex: 1 }}>
              <Text style={[s.ligneTitre, faite && s.ligneFaite]} numberOfLines={1}>{tr(m.titre)}</Text>
              {!!m.sousTitre && <Text style={s.ligneSous} numberOfLines={1}>{tr(m.sousTitre)}</Text>}
            </View>
            {faite && <Text style={s.coche}>✓</Text>}
            <TouchableOpacity style={s.btn} onPress={() => p.lancer(m)}>
              <Text style={s.btnTxt}>{faite ? t('apprentissage.refaire') : t('apprentissage.faire')}</Text>
            </TouchableOpacity>
          </View>
        );
      })}

      {/* Feuille tuto : Modal plein écran sur natif (section rendue dans Profil). */}
      {tutoOuvert && (Platform.OS === 'web' ? feuilleTuto : (
        <Modal visible transparent animationType="none" onRequestClose={() => setTutoOuvert(null)}>{feuilleTuto}</Modal>
      ))}
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  intro: { fontSize: 12.5, color: UI.muted, lineHeight: 18, marginBottom: 10 },
  dash: { flexDirection: 'row', gap: 12, marginBottom: 12 },
  progBloc: { flex: 1, backgroundColor: UI.card, borderRadius: 13, borderWidth: 1, borderColor: UI.border, padding: 12, alignItems: 'center' },
  anneau: { width: 74, height: 74, borderRadius: 37, borderWidth: 9, alignItems: 'center', justifyContent: 'center' },
  anneauCoiffe: { position: 'absolute', top: -9, left: -9, right: -9, bottom: -9, borderRadius: 37, borderWidth: 9 },
  anneauPct: { fontSize: 15, fontWeight: '800' },
  progLbl: { fontSize: 12.5, fontWeight: '800', color: UI.ink, marginTop: 8 },
  progSous: { fontSize: 10.5, color: UI.faint, marginTop: 1, textAlign: 'center' },
  barre: { alignSelf: 'stretch', height: 5, borderRadius: 3, backgroundColor: UI.champ, marginTop: 7, overflow: 'hidden' },
  barreFill: { height: 5, borderRadius: 3 },
  groupe: { fontSize: 12, fontWeight: '800', color: UI.muted, marginTop: 10, marginBottom: 6, letterSpacing: 0.3 },
  ligne: { flexDirection: 'row', alignItems: 'center', gap: 9, backgroundColor: UI.card, borderRadius: 11, borderWidth: 1, borderColor: UI.border, paddingHorizontal: 11, paddingVertical: 9, marginBottom: 6 },
  ligneEmoji: { fontSize: 16 },
  ligneTitre: { flex: 1, fontSize: 13, fontWeight: '700', color: UI.ink },
  ligneSous: { fontSize: 11, color: UI.faint, marginTop: 1 },
  ligneFaite: { color: UI.muted },
  coche: { fontSize: 13, color: '#3FB27F', fontWeight: '800' },
  btn: { backgroundColor: UI.accentTint, borderRadius: 9, paddingHorizontal: 12, paddingVertical: 6 },
  btnTxt: { fontSize: 12, fontWeight: '800', color: UI.accent },
  vide: { fontSize: 12, color: UI.faint, fontStyle: 'italic', marginBottom: 6 },
  etape: { flexDirection: 'row', gap: 10, marginBottom: 10, alignItems: 'flex-start' },
  etapeNum: { width: 22, height: 22, borderRadius: 11, backgroundColor: UI.accent, color: '#fff', fontSize: 12, fontWeight: '800', textAlign: 'center', lineHeight: 22, overflow: 'hidden' },
  etapeTxt: { flex: 1, fontSize: 13, color: UI.ink, lineHeight: 19 },
});
